import { styled } from '@mui/material/styles';

import { BRAND } from '../shared/theme/paletteDark';
import { FONTS } from '../shared/theme/typography';

/**
 * The header's pills (contract §15, §16): the Saved button and the one
 * view-specific button beside it — the map's glance, the list's sort, the
 * calendar's Share. One outline, one height, so the row reads as a set with
 * the city pill; filled parchment when `$on`.
 *
 * Their ground is a see-through dark rather than nothing, so they still read
 * over the map, where the header runs over the tiles on a gradient.
 */

/** Heights: the phone's to the Sept 23 reference, the strip's level with the city pill. */
export const CHROME_PILL_H = { phone: 28, desktop: 32 } as const;

export type ChromeSize = keyof typeof CHROME_PILL_H;

export const ChromePill = styled('button', { shouldForwardProp: (p) => p !== '$on' && p !== '$size' })<{
  $on?: boolean;
  $size: ChromeSize;
}>(({ $on = false, $size }) => ({
  appearance: 'none',
  cursor: 'pointer',
  flexShrink: 0,
  minWidth: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: $size === 'phone' ? 5 : 6,
  height: CHROME_PILL_H[$size],
  boxSizing: 'border-box',
  padding: $size === 'phone' ? '0 11px' : '0 14px',
  borderRadius: 999,
  border: `1.5px solid ${$on ? BRAND.parchment : 'rgba(243,238,231,0.7)'}`,
  background: $on ? BRAND.parchment : 'rgba(36,31,28,0.35)',
  color: $on ? BRAND.mocha : BRAND.parchment,
  fontFamily: FONTS.display,
  fontSize: $size === 'phone' ? 13 : 14,
  fontWeight: 700,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  WebkitTapHighlightColor: 'transparent',
  transition: 'background-color 160ms ease, color 160ms ease, border-color 120ms ease',
  '@media (hover: hover)': {
    '&:hover': {
      borderColor: BRAND.parchment,
      background: $on ? BRAND.parchment : 'rgba(243,238,231,0.12)',
    },
  },
  '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: 2 },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
}));
